import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileWarning, 
  Key, 
  ShieldAlert, 
  TrendingUp, 
  AlertTriangle, 
  Clock, 
  ArrowRight, 
  RefreshCw,
  BarChart3,
  PieChart,
  LineChart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { CredentialsApi, SecretsApi } from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import * as runtime from "api-client/runtime";
import CredentialLeaks from '@/components/CredentialLeaks';
import DataLeaks from '@/components/DataLeaks';

// Dummy data for the dashboard
const dummyStats = {
  totalPublicLeaks: 1585,
  totalStealerLeaks: 246,
  employeeStealerLeaks: 123,
  customerStealerLeaks: 123,
  totalDataLeaks: 7,
  leaksTrend: "-85% this month",
  criticalLeaks: 0,
  recentLeaks: 7,
  resolvedLeaks: 0,
  pendingLeaks: 7,
  leaksByType: [
    { type: "url", count: 7, percentage: 100 }
  ],
  mostLeakedEmails: [
    { email: "shahridzan.sihan@flawtrack.com", count: 8, percentage: 100 },
    { email: "adam.valentine@flawtrack.com", count: 8, percentage: 100 },
    { email: "durahman.hamdan@flawtrack.com", count: 6, percentage: 75 },
    { email: "sarinah.mohamad@flawtrack.com", count: 6, percentage: 75 },
    { email: "bahrin.antonio@flawtrack.com", count: 6, percentage: 75 },
    { email: "samsakiah.jamil@flawtrack.com", count: 5, percentage: 62 }
  ],
  recentLeaksList: [
    { id: "492", type: "Stealer Leak", description: "hassanali.sulaiman@flawtrack.com", timestamp: "2025-03-20T20:27:35.528420Z", severity: "medium" },
    { id: "491", type: "Stealer Leak", description: "pairinikaal.andraw@flawtrack.com", timestamp: "2025-03-20T20:27:35.514770Z", severity: "medium" },
    { id: "490", type: "Stealer Leak", description: "norlizah.mohdamin@flawtrack.com", timestamp: "2025-03-20T20:27:35.498952Z", severity: "medium" },
    { id: "489", type: "Stealer Leak", description: "norasma.jifli@flawtrack.com", timestamp: "2025-03-20T20:27:35.488402Z", severity: "medium" },
    { id: "488", type: "Stealer Leak", description: "norasma.jifli@flawtrack.com", timestamp: "2025-03-20T20:27:35.475280Z", severity: "medium" }
  ],
  monthlyTrends: [
    { month: "Oct", count: 0 },
    { month: "Nov", count: 0 },
    { month: "Dec", count: 0 },
    { month: "Jan", count: 0 },
    { month: "Feb", count: 1592 },
    { month: "Mar", count: 246 }
  ]
};

// Helper function to get severity badge variant
const getSeverityBadgeVariant = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    case 'low':
      return 'low';
    default:
      return 'default';
  }
};

// Helper function to format timestamp to relative time
const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 0) {
      return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    } else if (diffHour > 0) {
      return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    } else if (diffMin > 0) {
      return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (e) {
    console.error('Error formatting timestamp:', e);
    return timestamp;
  }
};

const DataProtectionDashboard = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);

  // Update the state type to match the sample data structure
  const [stats, setStats] = useState<{
    totalPublicLeaks: number;
    totalStealerLeaks: number;
    employeeStealerLeaks: number;
    customerStealerLeaks: number;
    totalDataLeaks: number;
    leaksTrend: string;
    criticalLeaks: number;
    recentLeaks: number;
    resolvedLeaks: number;
    pendingLeaks: number;
    leaksByType: { type: string; count: number; percentage: number }[];
    mostLeakedEmails: { email: string; count: number; percentage: number }[];
    recentLeaksList: { id: string; type: string; description: string; timestamp: string; severity?: string }[];
    monthlyTrends: { month: string; count: number }[];
  }>(dummyStats);

  const [credentialLeaksData, setCredentialLeaksData] = useState<any[]>([]);
  const [dataLeaksData, setDataLeaksData] = useState<any[]>([]);
  const [credentialLeaksCount, setCredentialLeaksCount] = useState<number>(0);
  const [dataLeaksCount, setDataLeaksCount] = useState<number>(0);
  const [newCredentialLeaks, setNewCredentialLeaks] = useState<number>(0);
  const [newDataLeaks, setNewDataLeaks] = useState<number>(0);

  const fetchStats = async () => {
    if (!currentTeam?.slug || !token) return;

    try {
      setIsRefreshing(true);
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || '';
      const url = `${baseUrl}/metrics/${encodeURIComponent(currentTeam.slug)}/leaks-dashboard`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText.substring(0, 100)}...`);
      }
      
      const responseText = await response.text();
      
      if (!responseText.trim()) {
        console.warn('Empty response received from API');
        return; // Keep using the dummy data
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parsing error:', parseError);
        console.error('Response that failed to parse:', responseText.substring(0, 200));
        throw new Error('Invalid JSON response from server');
      }
      
      setStats({
        totalPublicLeaks: data.totalPublicLeaks || dummyStats.totalPublicLeaks,
        totalStealerLeaks: data.totalStealerLeaks || dummyStats.totalStealerLeaks,
        totalDataLeaks: data.totalDataLeaks || dummyStats.totalDataLeaks,
        leaksTrend: data.leaksTrend || dummyStats.leaksTrend,
        criticalLeaks: data.criticalLeaks || dummyStats.criticalLeaks,
        recentLeaks: data.recentLeaks || dummyStats.recentLeaks,
        resolvedLeaks: data.resolvedLeaks || dummyStats.resolvedLeaks,
        pendingLeaks: data.pendingLeaks || dummyStats.pendingLeaks,
        leaksByType: data.leaksByType || dummyStats.leaksByType,
        mostLeakedEmails: data.mostLeakedEmails || dummyStats.mostLeakedEmails,
        recentLeaksList: data.recentLeaksList || dummyStats.recentLeaksList,
        monthlyTrends: data.monthlyTrends || dummyStats.monthlyTrends,
        employeeStealerLeaks: data.employeeStealerLeaks || dummyStats.employeeStealerLeaks,
        customerStealerLeaks: data.customerStealerLeaks || dummyStats.customerStealerLeaks
      });
    } catch (err) {
      console.error('Error fetching leaks dashboard stats:', err);
      // In case of error, keep using the dummy data (already set as default state)
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchCredentialLeaks = async () => {
      if (!currentTeam?.slug || !token) {
        return;
      }

      try {
        const api = new CredentialsApi(getApiConfiguration(token));
        const response = await api.publicLeaksList({
          teamSlug: currentTeam?.slug,
          page: 1,
        });

        // Transform the data to match the expected format for the CredentialLeaks component
        const transformedLeaks = response.results.slice(0, 5).map(leak => ({
          id: leak.id,
          type: leak.sourceName || 'Password Leak',
          source: leak.source || 'Unknown Source',
          email: leak.identifier || 'N/A',
          password: leak.password || 'N/A',
          date: leak.createdAt ? new Date(leak.createdAt).toISOString().split('T')[0] : 'N/A',
          severity: 'high' as 'high' | 'medium' | 'low' // Default to high severity
        }));

        setCredentialLeaksData(transformedLeaks);
        setCredentialLeaksCount(response.count || transformedLeaks.length);
        
        // Calculate new leaks (leaks from the last 24 hours)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const recentLeaks = response.results.filter(leak => 
          leak.createdAt && new Date(leak.createdAt) >= oneDayAgo
        ).length;
        setNewCredentialLeaks(recentLeaks);
      } catch (err) {
        console.error('Error fetching credential leaks:', err);
      }
    };

    const fetchDataLeaks = async () => {
      if (!currentTeam?.slug || !token) {
        return;
      }

      try {
        const secretsApi = new SecretsApi(getApiConfiguration(token));
        const response = await secretsApi.dataLeaksList({
          teamSlug: currentTeam?.slug,
          // Add any filtering parameters if needed
        });

        // Transform the data to match the expected format for the DataLeaks component
        console.log('Data leaks response:', response);
        const transformedLeaks = response.results.slice(0, 5).map(leak => ({
          id: leak.id,
          type: leak.leakType || 'Unknown',
          source: leak.sourceLink || 'Unknown Source',
          fileName: leak.otherInfo?.file || 'Unknown File',
          tags: [],
          date: leak.createdAt ? new Date(leak.createdAt).toISOString().split('T')[0] : 'N/A',
          severity: (leak.severity || 'high') as 'high' | 'medium' | 'low'
        }));

        setDataLeaksData(transformedLeaks);
        setDataLeaksCount(response.count || transformedLeaks.length);
        
        // Calculate new leaks (leaks from the last 24 hours)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const recentLeaks = response.results.filter(leak => 
          leak.createdAt && new Date(leak.createdAt) >= oneDayAgo
        ).length;
        setNewDataLeaks(recentLeaks);
      } catch (err) {
        console.error('Error fetching data leaks:', err);
      }
    };

    fetchDataLeaks();

    fetchCredentialLeaks();
    fetchStats();

  }, [currentTeam?.slug, token]);

  const handleRefresh = () => {
    fetchStats();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ShieldAlert className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Data Protection Dashboard</h1>
            <p className="text-sm text-gray-400">Overview of all data protection metrics and alerts</p>
          </div>
        </div>
        {/* <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div> */}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Public Leaks</span>
            <Key className="h-5 w-5 text-blue-500" />
          </div>
          <div className="flex items-end justify-between">
            <span className="text-2xl font-bold text-white">{stats.totalPublicLeaks}</span>
            <Link to="/data-protection/public-leaks" className="text-blue-500 text-sm flex items-center">
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </div>

        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Stealer Leaks</span>
            <Key className="h-5 w-5 text-blue-500" />
          </div>
          <div className="flex items-end justify-between">
            <span className="text-2xl font-bold text-white">{stats.totalStealerLeaks}</span>
            <Link to="/data-protection/stealer-leaks" className="text-blue-500 text-sm flex items-center">
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </div>

        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Data Leaks</span>
            <FileWarning className="h-5 w-5 text-blue-500" />
          </div>
          <div className="flex items-end justify-between">
            <span className="text-2xl font-bold text-white">{stats.totalDataLeaks}</span>
            <Link to="/data-protection/data-leaks" className="text-blue-500 text-sm flex items-center">
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </div>

        {/* <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Critical Leaks</span>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="flex items-end justify-between">
            <span className="text-2xl font-bold text-white">{stats.criticalLeaks}</span>
            <span className="text-red-500 text-sm flex items-center">
              {stats.criticalLeaks > 0 ? 'Needs attention' : 'All clear'}
            </span>
          </div>
        </div> */}
      </div>

      {/* Charts and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stealer Leaks Stats Column */}
        <div className="space-y-6">
          {/* Employee Stealer Leaks Card */}
          <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Employee Stealer Leaks</h3>
                <p className="text-sm text-gray-400">Affecting employees</p>
              </div>
              <Key className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-white">{stats.employeeStealerLeaks}</span>
              <span className="text-xs text-blue-400">{((stats.employeeStealerLeaks / stats.totalStealerLeaks) * 100).toFixed(1)}%</span>
            </div>
            <div className="mt-2 w-full bg-gray-800 rounded-full h-1.5">
              <div 
                className="bg-blue-500 h-1.5 rounded-full" 
                style={{ width: `${(stats.employeeStealerLeaks / stats.totalStealerLeaks) * 100}%` }}
              ></div>
            </div>
            <div className="mt-4">
              <Link to="/data-protection/stealer-leaks?type=employee" className="text-blue-500 text-sm flex items-center">
                View details <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </div>
          </div>

          {/* Customer Stealer Leaks Card */}
          <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Customer Stealer Leaks</h3>
                <p className="text-sm text-gray-400">Affecting customers</p>
              </div>
              <Key className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-white">{stats.customerStealerLeaks}</span>
              <span className="text-xs text-blue-400">{((stats.customerStealerLeaks / stats.totalStealerLeaks) * 100).toFixed(1)}%</span>
            </div>
            <div className="mt-2 w-full bg-gray-800 rounded-full h-1.5">
              <div 
                className="bg-blue-500 h-1.5 rounded-full" 
                style={{ width: `${(stats.customerStealerLeaks / stats.totalStealerLeaks) * 100}%` }}
              ></div>
            </div>
            <div className="mt-4">
              <Link to="/data-protection/stealer-leaks?type=customer" className="text-blue-500 text-sm flex items-center">
                View details <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </div>
          </div>
        </div>

        {/* Most Leaked Emails */}
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Most Leaked Emails</h3>
              <p className="text-sm text-gray-400">Top compromised email addresses</p>
            </div>
            <Key className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {stats.mostLeakedEmails.map((item, index) => (
              <div key={index} className="space-y-1">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="text-sm text-white truncate max-w-[200px]">{item.email}</span>
                    <span className="ml-2 text-xs text-gray-400">{item.count} leaks</span>
                  </div>
                  {/* <span className="text-xs text-gray-400">{item.percentage}%</span> */}
                </div>
                <div className="w-full bg-gray-800 rounded-full h-1.5">
                  <div 
                    className="bg-blue-500 h-1.5 rounded-full" 
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Charts and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CredentialLeaks
          leaks={credentialLeaksData.slice(0, 3).map(leak => ({
            ...leak,
            severity: leak.severity as 'high' | 'medium' | 'low'
          }))}
          totalLeaks={credentialLeaksCount}
          newLeaks={newCredentialLeaks || credentialLeaksCount}
        />
        <DataLeaks
          leaks={dataLeaksData.slice(0, 3).map(leak => ({
            ...leak,
            severity: leak.severity as 'high' | 'medium' | 'low'
          }))}
          totalLeaks={dataLeaksCount}
          newLeaks={newDataLeaks || dataLeaksCount}
        />
      </div>

      {/* Recent Leaks Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Recent Leaks</h3>
            <p className="text-sm text-gray-400">Latest detected leaks across all sources</p>
          </div>
          <Clock className="h-5 w-5 text-gray-400" />
        </div>
        <table className="w-full">
          <thead className="text-gray-400 text-xs uppercase">
            <tr>
              <th className="px-6 py-3 text-left">Type</th>
              <th className="px-6 py-3 text-left">Description</th>
              <th className="px-6 py-3 text-left">Severity</th>
              <th className="px-6 py-3 text-left">Time</th>
              <th className="px-6 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {stats.recentLeaksList.map((leak) => (
              <tr key={leak.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <span className="text-sm text-white">{leak.type}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-white truncate max-w-[200px] block">{leak.description}</span>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={getSeverityBadgeVariant(leak.severity || 'medium')}>
                    {leak.severity || 'Medium'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400">{formatTimestamp(leak.timestamp)}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-400">
                    View Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataProtectionDashboard;
